import iconv from 'iconv-lite';

/**
 * Decodes PDF data from API response to a buffer
 * Handles different response formats to prevent black PDF display issues
 * 
 * @param {string} pdfData - PDF data from API response (can be string, base64, or binary)
 * @returns {Buffer|null} - Decoded PDF buffer, or null if invalid
 */
export const decodePdfToBuffer = (pdfData) => {
  if (!pdfData || pdfData === null || pdfData === undefined) {
    return null;
  }

  // If it's already a blob URL, we can't decode to buffer
  if (typeof pdfData === 'string' && pdfData.startsWith('blob:')) {
    return null;
  }

  try {
    let buffer;
    let isBufferType = false;
    
    // Check if the data is already a Buffer or Uint8Array
    // Use try-catch for each check to prevent any errors from breaking the flow
    try {
      if (typeof Buffer !== 'undefined' && typeof Buffer.isBuffer === 'function') {
        try {
          const isBuffer = Buffer.isBuffer(pdfData);
          if (isBuffer) {
            buffer = Buffer.from(pdfData);
            isBufferType = true;
          }
        } catch (e) {
          // Buffer.isBuffer check failed, continue
        }
      }
    } catch (e) {
      // Error accessing Buffer, continue
    }
    
    if (!isBufferType) {
      try {
        if (typeof Uint8Array !== 'undefined' && pdfData instanceof Uint8Array) {
          buffer = Buffer.from(pdfData);
          isBufferType = true;
        }
      } catch (e) {
        // instanceof check failed, continue
      }
    }
    
    if (!isBufferType) {
      try {
        if (typeof ArrayBuffer !== 'undefined' && pdfData instanceof ArrayBuffer) {
          buffer = Buffer.from(new Uint8Array(pdfData));
          isBufferType = true;
        }
      } catch (e) {
        // instanceof check failed, continue
      }
    }
    
    // If we got a buffer, validate and return it
    if (isBufferType && buffer && buffer.length > 0) {
      return buffer;
    } 
    // Check if it's a string
    if (typeof pdfData === 'string') {
      // Try to detect if it's base64
      const base64Pattern = /^[A-Za-z0-9+/=]+$/;
      const isBase64 = base64Pattern.test(pdfData) && pdfData.length > 100;
      
      if (isBase64 && !pdfData.includes('\n') && !pdfData.includes('\r')) {
        // It's likely base64 encoded - try decoding it
        try {
          const base64Buffer = Buffer.from(pdfData, 'base64');
          // Validate: check if decoded result looks like a PDF
          const header = base64Buffer.slice(0, 4).toString('ascii');
          if (header === '%PDF') {
            buffer = base64Buffer;
          } else {
            // Base64 decode didn't produce a valid PDF, fall back to iconv
            buffer = iconv.encode(pdfData, 'iso-8859-1');
          }
        } catch (e) {
          // Fall back to iconv encoding if base64 decode fails
          buffer = iconv.encode(pdfData, 'iso-8859-1');
        }
      } else {
        // Treat as binary string and encode using iconv
        // This handles the case where the server sends PDF as a string
        buffer = iconv.encode(pdfData, 'iso-8859-1');
      }
    } else {
      // Unknown format - try to convert to buffer as last resort
      try {
        buffer = Buffer.from(pdfData);
      } catch (e) {
        console.error('Unable to convert PDF data to buffer. Type:', typeof pdfData, 'Error:', e);
        return null;
      }
    }

    // Validate that we have a valid PDF by checking the PDF header
    const pdfHeader = buffer.slice(0, 4).toString('ascii');
    if (pdfHeader !== '%PDF') {
      // If it doesn't start with %PDF, it might be encoded differently
      // Try decoding from latin1/iso-8859-1 first
      const decoded = iconv.decode(buffer, 'iso-8859-1');
      const reEncoded = iconv.encode(decoded, 'iso-8859-1');
      
      // Check again
      const reEncodedHeader = reEncoded.slice(0, 4).toString('ascii');
      if (reEncodedHeader === '%PDF') {
        buffer = reEncoded;
      } else {
        // Last resort: use the original buffer
        console.warn('PDF data may be corrupted or in unexpected format');
      }
    }

    return buffer;
  } catch (error) {
    console.error('Error decoding PDF:', error);
    return null;
  }
};

/**
 * Decodes PDF data from API response and creates a blob URL
 * Handles different response formats to prevent black PDF display issues
 * 
 * @param {string} pdfData - PDF data from API response (can be string, base64, or binary)
 * @returns {string|null} - Object URL for the PDF blob, or null if invalid
 */
export const decodePdfToBlobUrl = (pdfData) => {
  // Already a blob URL (e.g. from streamed PDF: 200, Content-Type: application/pdf, Content-Disposition: inline)
  if (typeof pdfData === 'string' && pdfData.startsWith('blob:')) {
    return pdfData;
  }

  const buffer = decodePdfToBuffer(pdfData);
  if (!buffer) {
    return null;
  }

  try {
    // Create blob and object URL
    const blob = new Blob([buffer], { type: 'application/pdf' });
    return window.URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error creating PDF blob URL:', error);
    return null;
  }
};

/**
 * Revokes a PDF blob URL to free memory
 * 
 * @param {string} url - The object URL to revoke
 */
export const revokePdfBlobUrl = (url) => {
  if (url && typeof url === 'string' && url.startsWith('blob:')) {
    try {
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error revoking PDF blob URL:', error);
    }
  }
};

/**
 * Checks if a value contains PDF data
 * @param {*} value - Value to check
 * @returns {boolean} True if value contains PDF data
 */
export const hasPdfData = (value) => {
  return (
    typeof value === 'object' &&
    value !== null &&
    value.pdf &&
    typeof value.pdf === 'string'
  );
};

/**
 * Detects if a string is base64 encoded PDF data
 * @param {string} pdfData - PDF data string to check
 * @returns {boolean} True if the string appears to be base64 encoded
 */
export const isBase64Pdf = (pdfData) => {
  if (typeof pdfData !== 'string' || pdfData.length < 100) {
    return false;
  }
  const base64Pattern = /^[A-Za-z0-9+/=]+$/;
  return base64Pattern.test(pdfData) && !pdfData.includes('\n') && !pdfData.includes('\r');
};

/**
 * Calculates the memory size in bytes of PDF data
 * Handles different PDF data formats (base64, binary strings, objects with pdf property)
 * 
 * @param {*} value - PDF data (can be string, object with pdf property, or blob URL)
 * @returns {number} Size in bytes
 */
export const calculatePdfSize = (value) => {
  // Handle blob URLs (just a reference, not the actual data)
  if (typeof value === 'string' && value.startsWith('blob:')) {
    return 1000; // Blob URL reference size
  }

  // Handle PDF data in response objects (e.g., { pdf: "base64_or_binary_string" })
  if (hasPdfData(value)) {
    const pdfData = value.pdf;
    if (typeof pdfData === 'string') {
      // Check if it's base64 encoded
      if (isBase64Pdf(pdfData)) {
        // Base64 string in JS uses 2 bytes per char (UTF-16), but represents 3/4 of that in binary
        // The memory footprint is: pdfData.length * 2 bytes
        return Math.max(1, pdfData.length * 2);
      } else {
        // Binary string - each character represents a byte, but stored as UTF-16 (2 bytes per char)
        return Math.max(1, pdfData.length * 2);
      }
    }
  }

  // Handle direct PDF strings
  if (typeof value === 'string') {
    if (isBase64Pdf(value)) {
      return Math.max(1, value.length * 2);
    } else {
      return Math.max(1, value.length * 2);
    }
  }

  // Not PDF data or unknown format
  return 0;
};
